import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    IconCheck, IconChevronDown, IconCopy, IconDownload, IconLink, IconPhoto, IconSparkles, IconX,
} from '@tabler/icons-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { notifications } from '@/lib/notifications';
import aiTemplateService from '../../../services/aiTemplateService';

// Caption tones offered to the admin. 'auto' lets the model pick a tone that
// fits the template; the rest force one. Keep in sync with the backend's
// ALLOWED_MODES in routes/ai-templates.js (share-caption endpoint).
const CAPTION_MODES = [
    'auto', 'funny', 'dramatic', 'energetic', 'romantic', 'religious', 'attitude',
    'emotional', 'motivational', 'nostalgic', 'cute', 'elegant', 'festive', 'sad', 'poetic',
];

// Radix dropdown styled like a select. SelectField can't be used here: its menu
// is position:fixed, and the dialog's centering transform makes the dialog the
// containing block for fixed descendants, so the menu lands off-screen. Radix
// DropdownMenu portals to <body> and layers correctly inside the Dialog.
function OptionDropdown({ value, onChange, options }) {
    const selected = options.find((o) => o.value === value);
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    className="w-full h-9 px-3 flex items-center justify-between gap-2 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-[12.5px] text-zinc-700 dark:text-zinc-200"
                >
                    <span className="truncate">{selected?.label}</span>
                    <IconChevronDown size={15} className="flex-shrink-0 opacity-50" />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="font-hind z-[100] max-h-64 overflow-y-auto min-w-[var(--radix-dropdown-menu-trigger-width)]">
                {options.map((opt) => (
                    <DropdownMenuItem key={opt.value} onSelect={() => onChange(opt.value)} className="gap-2 text-[12.5px]">
                        <span className="w-4 flex-shrink-0 text-primary">{opt.value === value && <IconCheck size={14} />}</span>
                        {opt.label}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

/**
 * Share dialog for an AI template (admin promo): generate an AI caption
 * (language + tone options), copy the user-side template link, and copy or
 * download the thumbnail image — everything needed for a social post.
 */
export default function AiTemplateShareModal({ template, open, onOpenChange }) {
    const { t } = useTranslation();
    const [language, setLanguage] = useState('bn');
    const [mode, setMode] = useState('auto');
    const [caption, setCaption] = useState('');
    const [generating, setGenerating] = useState(false);
    // Briefly flags which item was copied ('caption' | 'link' | 'image' | 'download').
    const [copied, setCopied] = useState(null);
    const [copyingImage, setCopyingImage] = useState(false);
    const [downloadingImage, setDownloadingImage] = useState(false);

    // Fresh state each time the dialog opens (possibly for a different template).
    useEffect(() => {
        if (open) { setCaption(''); setLanguage('bn'); setMode('auto'); setCopied(null); }
    }, [open, template?.id]);

    const imageSrc = template?.thumbnail || null;
    // Hashtags from the template's own tags + the brand tag.
    const hashtags = [
        ...(Array.isArray(template?.tags) ? template.tags.slice(0, 4) : []),
        'SohozKaj',
    ].map((tg) => `#${String(tg).replace(/\s+/g, '')}`).join(' ');
    const captionFull = caption ? `${caption}${hashtags ? `\n\n${hashtags}` : ''}` : '';
    // The public user-side page for this template — pasteable into a social post.
    const templateUrl = template?.slug ? `${window.location.origin}/ai-templates/${template.slug}` : '';

    const languageOptions = [
        { value: 'bn', label: t('aiTemplatesAdmin.shareLangBn') },
        { value: 'en', label: t('aiTemplatesAdmin.shareLangEn') },
    ];
    const modeOptions = CAPTION_MODES.map((m) => ({
        value: m,
        label: t(`aiTemplatesAdmin.shareMode_${m}`),
    }));

    const flashCopied = (key) => {
        setCopied(key);
        setTimeout(() => setCopied((c) => (c === key ? null : c)), 1800);
    };

    const handleGenerate = async () => {
        if (!template?.id || generating) return;
        setGenerating(true);
        try {
            const res = await aiTemplateService.generateShareCaption({ templateId: template.id, language, mode });
            setCaption(String(res?.data?.caption || '').trim());
        } catch {
            notifications.show({ message: t('aiTemplatesAdmin.shareCaptionFailed'), color: 'red' });
        } finally {
            setGenerating(false);
        }
    };

    const handleCopyCaption = async () => {
        if (!captionFull) return;
        try { await navigator.clipboard.writeText(captionFull); flashCopied('caption'); }
        catch { notifications.show({ message: t('worldCupJersey.share.copyFailed'), color: 'red' }); }
    };
    const handleCopyLink = async () => {
        if (!templateUrl) return;
        try { await navigator.clipboard.writeText(templateUrl); flashCopied('link'); }
        catch { notifications.show({ message: t('worldCupJersey.share.copyFailed'), color: 'red' }); }
    };

    // Copy the actual image bytes to the clipboard (as PNG, the widely-supported type)
    // so it can be pasted straight into a social post.
    //
    // We CORS-fetch the bytes (cache-busted so we don't reuse the non-CORS <img>
    // cache entry, which would otherwise taint the canvas and block the copy), then
    // normalise to PNG (the type ClipboardItem reliably accepts). The blob-producing
    // work is handed to ClipboardItem as a promise so the clipboard write stays within
    // the click's user-activation window (Chrome/Safari requirement).
    const buildSharePngBlob = async () => {
        const bust = imageSrc + (imageSrc.includes('?') ? '&' : '?') + 'copy=1';
        const res = await fetch(bust, { mode: 'cors', cache: 'no-store' });
        if (!res.ok) throw new Error('image fetch failed');
        const srcBlob = await res.blob();
        if (srcBlob.type === 'image/png') return srcBlob;
        // Convert via an ImageBitmap → canvas. The source is same-origin blob data now,
        // so the canvas is NOT tainted and toBlob succeeds.
        const bmp = await createImageBitmap(srcBlob);
        const canvas = document.createElement('canvas');
        canvas.width = bmp.width; canvas.height = bmp.height;
        canvas.getContext('2d').drawImage(bmp, 0, 0);
        return await new Promise((resolve, reject) =>
            canvas.toBlob((b) => b ? resolve(b) : reject(new Error('encode failed')), 'image/png'));
    };
    const handleCopyImage = async () => {
        if (!imageSrc || copyingImage) return;
        setCopyingImage(true);
        try {
            await navigator.clipboard.write([
                new window.ClipboardItem({ 'image/png': buildSharePngBlob() }),
            ]);
            flashCopied('image');
        } catch {
            notifications.show({ message: t('worldCupJersey.share.imageCopyFailed'), color: 'red' });
        } finally {
            setCopyingImage(false);
        }
    };
    // Download the same normalised PNG the copy path produces, named after the template.
    const handleDownloadImage = async () => {
        if (!imageSrc || downloadingImage) return;
        setDownloadingImage(true);
        try {
            const blob = await buildSharePngBlob();
            const slug = template?.slug
                || (template?.name || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
                || 'ai-template';
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${slug}.png`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            flashCopied('download');
        } catch {
            notifications.show({ message: t('worldCupJersey.share.downloadFailed'), color: 'red' });
        } finally {
            setDownloadingImage(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent showClose={false} className="max-w-2xl max-h-[92vh] overflow-y-auto">
                {/* Custom close — red, larger hit area */}
                <button
                    onClick={() => onOpenChange(false)}
                    aria-label={t('common.close')}
                    className="absolute right-4 top-4 w-8 h-8 rounded-lg bg-red-500 hover:bg-red-600 text-white flex items-center justify-center transition-colors"
                >
                    <IconX size={16} />
                </button>
                <DialogHeader>
                    <DialogTitle>{t('worldCupJersey.share.button')}</DialogTitle>
                </DialogHeader>

                <div className="grid gap-4 sm:grid-cols-[260px_minmax(0,1fr)]">
                    {/* Template image */}
                    {imageSrc && (
                        <div className="rounded-lg overflow-hidden border border-zinc-200 dark:border-zinc-700 flex items-center justify-center bg-zinc-50 dark:bg-zinc-800 self-start">
                            <img src={imageSrc} alt="" className="max-h-[400px] w-auto object-contain" />
                        </div>
                    )}

                    {/* AI caption generator */}
                    <div className="flex flex-col gap-3 min-w-0">
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="block mb-1 text-[11.5px] font-medium text-zinc-500 dark:text-zinc-400">
                                    {t('aiTemplatesAdmin.shareCaptionLanguage')}
                                </label>
                                <OptionDropdown value={language} onChange={setLanguage} options={languageOptions} />
                            </div>
                            <div>
                                <label className="block mb-1 text-[11.5px] font-medium text-zinc-500 dark:text-zinc-400">
                                    {t('aiTemplatesAdmin.shareCaptionMode')}
                                </label>
                                <OptionDropdown value={mode} onChange={setMode} options={modeOptions} />
                            </div>
                        </div>

                        <button
                            onClick={handleGenerate}
                            disabled={generating}
                            className="w-full inline-flex items-center justify-center gap-1.5 h-10 px-3 rounded-lg bg-primary hover:bg-primary/90 text-white text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {generating
                                ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                : <IconSparkles size={16} />}
                            {caption ? t('aiTemplatesAdmin.shareCaptionRegenerate') : t('aiTemplatesAdmin.shareCaptionGenerate')}
                        </button>

                        {caption ? (
                            <div className="flex-1 flex flex-col rounded-lg border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/60 p-3">
                                {/* Editable so the admin can tweak before copying */}
                                <textarea
                                    value={caption}
                                    onChange={(e) => setCaption(e.target.value)}
                                    rows={5}
                                    className="w-full flex-1 resize-none bg-transparent text-[13px] leading-snug text-zinc-700 dark:text-zinc-200 focus:outline-none"
                                />
                                {hashtags && (
                                    <p className="mt-1 text-[11px] text-primary/80 break-words">{hashtags}</p>
                                )}
                                <div className="mt-2 flex justify-end">
                                    <button
                                        onClick={handleCopyCaption}
                                        className="inline-flex items-center gap-1.5 text-[12px] font-medium text-primary hover:text-primary/80 transition-colors">
                                        {copied === 'caption' ? <IconCheck size={14} className="text-emerald-500" /> : <IconCopy size={14} />}
                                        {copied === 'caption' ? t('worldCupJersey.share.copied') : t('worldCupJersey.share.copyCaption')}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="flex-1 min-h-[130px] rounded-lg border border-dashed border-zinc-200 dark:border-zinc-700 flex items-center justify-center p-4 text-center text-xs text-zinc-400 dark:text-zinc-500">
                                {t('aiTemplatesAdmin.shareCaptionHint')}
                            </div>
                        )}
                    </div>
                </div>

                {/* Actions: copy image · copy link · download */}
                <div className="mt-4 flex flex-wrap items-center gap-2">
                    <button
                        onClick={handleCopyImage}
                        disabled={copyingImage || !imageSrc}
                        className="flex-1 min-w-[160px] inline-flex items-center justify-center gap-1.5 h-10 px-3 rounded-lg bg-primary hover:bg-primary/90 text-white text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                        {copyingImage
                            ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            : copied === 'image' ? <IconCheck size={16} /> : <IconPhoto size={16} />}
                        {copied === 'image' ? t('worldCupJersey.share.imageCopied') : t('worldCupJersey.share.copyImage')}
                    </button>
                    {templateUrl && (
                        <button
                            onClick={handleCopyLink}
                            className="inline-flex items-center justify-center gap-1.5 h-10 px-4 rounded-lg border border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 text-sm font-semibold hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors">
                            {copied === 'link' ? <IconCheck size={16} className="text-emerald-500" /> : <IconLink size={16} />}
                            {copied === 'link' ? t('worldCupJersey.share.linkCopied') : t('worldCupJersey.share.copyLink')}
                        </button>
                    )}
                    <button
                        onClick={handleDownloadImage}
                        disabled={downloadingImage || !imageSrc}
                        className="inline-flex items-center justify-center gap-1.5 h-10 px-4 rounded-lg border border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 text-sm font-semibold hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                        {downloadingImage
                            ? <span className="w-4 h-4 border-2 border-zinc-400 border-t-transparent rounded-full animate-spin" />
                            : copied === 'download' ? <IconCheck size={16} className="text-emerald-500" /> : <IconDownload size={16} />}
                        {t('common.download')}
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
